/**
 * AuthContext.js
 * 
 * Context provider for authentication state.
 * Manages user authentication and subscription status.
 */
import React, { createContext, useState, useEffect } from 'react';

// Create the context
export const AuthContext = createContext(null);

/**
 * Authentication context provider
 * @param {Object} props - Component props
 * @param {AuthService} props.authService - Authentication service instance
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ authService, children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if user is authenticated
        const authStatus = authService.isAuthenticated();
        setIsAuthenticated(authStatus);

        if (authStatus) {
          // Get user data if authenticated
          setUser(authService.getCurrentUser());
        }
      } catch (error) {
        console.error('Authentication check error:', error);
        // Handle authentication error (clear invalid state)
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [authService]);

  // Login handler
  const login = async (credentials) => {
    try {
      const userData = await authService.login(credentials);
      setIsAuthenticated(true);
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout handler
  const logout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  // Update subscription
  const updateSubscription = async (subscriptionDetails) => {
    try {
      const result = await authService.updateSubscription(subscriptionDetails);
      
      // Update user data with new subscription
      if (result.success && user) {
        setUser({
          ...user,
          subscription: result.subscription
        });
      }
      
      return result;
    } catch (error) {
      console.error('Subscription update error:', error);
      return { success: false, error: error.message };
    }
  };

  // Calculate premium status
  const isPremium = isAuthenticated && user?.subscription === 'premium';

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        user, 
        isPremium,
        login, 
        logout,
        updateSubscription,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
